import { shapeHolesOf, shapeRingOf } from '@repo/api-contracts';
import type { PhotoDimensions } from '@repo/design-renderer';
import type { ReactElement } from 'react';
import { type WarpedSlot, warpedRingPieces } from '@/features/design-editor';
import { cn } from '@/shared/ui';
import type { PhotoBox } from './warped-selection-overlay';

interface WarpedAreaOutlineProps {
  readonly slot: WarpedSlot;
  readonly photo: PhotoDimensions;
  readonly box: PhotoBox;
  /** 지금 편집 중인 영역 — 점선으로 보인다. */
  readonly active: boolean;
  /** 이 영역에서 노드를 골랐을 때 — 편집 중인 영역이면 테두리를 진하게 보인다. */
  readonly emphasized: boolean;
}

/** 인쇄 영역 모양(바깥 고리·구멍)을 휜 사진 위 선으로 — 평면 편집의 AreaShapeOutline 과 같은 결의 테두리. */
export const WarpedAreaOutline = ({
  slot,
  photo,
  box,
  active,
  emphasized,
}: WarpedAreaOutlineProps): ReactElement => {
  const k = box.width / photo.widthPx;
  const { widthMm, heightMm } = slot.component;
  const step = Math.max(0.5, Math.max(widthMm, heightMm) / 64);
  const pieces = [shapeRingOf(slot.component), ...shapeHolesOf(slot.component)].flatMap((ring) =>
    warpedRingPieces(slot, photo, ring, step),
  );
  const strong = active && emphasized;
  return (
    <svg
      aria-hidden
      className="pointer-events-none absolute inset-0 size-full"
      data-testid={`warped-area-${slot.component.id}`}
    >
      {pieces.map((piece, index) => {
        const Shape = piece.closed ? 'polygon' : 'polyline';
        return (
          <Shape
            // biome-ignore lint/suspicious/noArrayIndexKey: 조각은 순서만 있고 다른 식별자가 없다.
            key={index}
            points={piece.points
              .map((point) => `${box.left + point.x * k},${box.top + point.y * k}`)
              .join(' ')}
            fill="none"
            strokeWidth={1}
            strokeDasharray={active ? '4 3' : undefined}
            className={cn(
              !active && 'stroke-foreground/15',
              active && !strong && 'stroke-foreground/35',
              strong && 'stroke-foreground/60',
            )}
          />
        );
      })}
    </svg>
  );
};
